import ReactGA from 'react-ga4';

import { app_config } from '../api/app-config';

let isGAInitialized = false;

export const initGA = () => {
    if (isGAInitialized) return;

    console.log('[GA] Initializing with measurement ID:', app_config.gaMeasurementId);
    ReactGA.initialize(app_config.gaMeasurementId);
    isGAInitialized = true;
};

// Track page views
export const trackPageView = (path: string, title?: string) => {
    console.log('[GA Pageview]', { path, title: title || path });
    ReactGA.send({ hitType: 'pageview', page: path, title: title || path });
};

/**
 * Send event to GA4 with rich parameters for dashboard reporting.
 * Uses GA4 event(name, params) format so parameters appear in Reports > Engagement > Events.
 */
const sendEvent = (eventName: string, params: Record<string, string | number | boolean | undefined>) => {
    const cleaned = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '')) as Record<
        string,
        string | number | boolean
    >;
    console.log('[GA Event]', eventName, cleaned);
    ReactGA.event(eventName, cleaned);
};

/** Caps a free-text analytics param's length so a long user-entered value can't bloat the event payload. */
const truncate = (value: string, max = 200): string => (value.length > max ? value.slice(0, max) : value);

export const trackMakePaymentClick = (source: string) => {
    sendEvent('make_payment_click', {
        event_category: 'Dashboard',
        click_source: source,
        description: `Make Payment clicked from ${source}`,
    });
};

// Contract Events
export const trackContractSelected = (contractId: string, source: string) => {
    sendEvent('contract_selected', {
        event_category: 'Contracts',
        contract_id: contractId,
        source,
        description: `Contract selected: ${contractId} from ${source}`,
    });
};

export const trackContractTabViewed = (tab: string, contractId: string) => {
    sendEvent('contract_tab_viewed', {
        event_category: 'Contracts',
        tab,
        contract_id: contractId,
        description: `Contract tab viewed: ${tab}`,
    });
};

export const trackMakePaymentCtaClick = (contractId: string, source: string) => {
    sendEvent('make_payment_cta_click', {
        event_category: 'Contracts',
        contract_id: contractId,
        source,
        description: `Make Payment CTA clicked from ${source}`,
    });
};

export const trackDocumentSearchUsed = (contractId: string, hasResults: boolean) => {
    sendEvent('document_search_used', {
        event_category: 'Contracts',
        contract_id: contractId,
        has_results: hasResults,
        description: 'Document search used',
    });
};

export const trackPaymentHistorySearchUsed = (contractId: string, hasResults: boolean) => {
    sendEvent('payment_history_search_used', {
        event_category: 'Contracts',
        contract_id: contractId,
        has_results: hasResults,
        description: 'Payment history search used',
    });
};

// Payment Events
export type PaymentType = 'one_time' | 'scheduled' | 'bulk' | 'ello';

export const trackPaymentInitiated = (amount: number, contractId: string, paymentType?: PaymentType) => {
    sendEvent('payment_initiated', {
        event_category: 'Payments',
        contract_id: contractId,
        amount,
        value: amount,
        currency: 'USD',
        ...(paymentType && { payment_type: paymentType }),
        description: `Payment initiated for contract ${contractId}`,
    });
};

export const trackPaymentCompleted = (
    amount: number,
    contractId: string,
    paymentMethod: string,
    paymentType?: PaymentType,
    transactionId?: string,
) => {
    sendEvent('payment_completed', {
        event_category: 'Payments',
        contract_id: contractId,
        amount,
        value: amount,
        currency: 'USD',
        payment_method: paymentMethod,
        ...(paymentType && { payment_type: paymentType }),
        ...(transactionId && { transaction_id: transactionId }),
        description: `Payment completed - ${paymentMethod} for contract ${contractId}`,
    });
};

export const trackPaymentFailed = (contractId: string, reason?: string, paymentType?: PaymentType) => {
    sendEvent('payment_failed', {
        event_category: 'Payments',
        contract_id: contractId,
        failure_reason: reason || 'Unknown',
        ...(paymentType && { payment_type: paymentType }),
        description: `Payment failed for contract ${contractId}`,
    });
};

export const trackPaymentCancelled = (contractId: string) => {
    sendEvent('payment_cancelled', {
        event_category: 'Payments',
        contract_id: contractId,
        description: `Scheduled payment cancelled for contract ${contractId}`,
    });
};

// Support Events
export const trackSupportRequestStarted = (entryPoint: string) => {
    sendEvent('support_request_started', {
        event_category: 'Support',
        entry_point: entryPoint,
        description: 'Support request form opened',
    });
};

export const trackSupportTicketCreated = (requestType: string, contractId?: string, subject?: string) => {
    sendEvent('support_ticket_created', {
        event_category: 'Support',
        request_type: requestType,
        ...(contractId && { contract_id: contractId }),
        ...(subject && { subject: truncate(subject, 100) }),
        description: 'Support ticket created',
    });
};

export const trackSupportTicketCreationFailed = (reason?: string) => {
    sendEvent('support_ticket_creation_failed', {
        event_category: 'Support',
        ...(reason && { reason: truncate(reason) }),
        description: 'Support ticket creation failed',
    });
};

// Document Events
export const trackDocumentDownload = (documentName: string, contractId?: string) => {
    sendEvent('document_download', {
        event_category: 'Documents',
        document_name: truncate(documentName, 100),
        ...(contractId && { contract_id: contractId }),
        description: 'Document downloaded',
    });
};

export const trackDocumentPreview = (documentName: string, contractId?: string) => {
    sendEvent('document_preview', {
        event_category: 'Documents',
        document_name: truncate(documentName, 100),
        ...(contractId && { contract_id: contractId }),
        description: 'Document preview opened',
    });
};

export const trackDocumentUpload = (documentName: string, contractId?: string, fileType?: string) => {
    sendEvent('document_upload', {
        event_category: 'Documents',
        document_name: truncate(documentName, 100),
        ...(contractId && { contract_id: contractId }),
        ...(fileType && { file_type: fileType }),
        description: 'Document uploaded',
    });
};

export const trackDocumentActionFailed = (
    action: 'download' | 'preview' | 'upload',
    reason?: string,
    contractId?: string,
) => {
    sendEvent('document_action_failed', {
        event_category: 'Documents',
        action,
        ...(reason && { reason: truncate(reason) }),
        ...(contractId && { contract_id: contractId }),
        description: `Document ${action} failed`,
    });
};

// Bank Account Events - consolidated: one event name per outcome, sliced by `action` param
export const trackBankAccountAction = (
    action: 'added' | 'activated' | 'deactivated',
    source?: string,
    bankName?: string,
) => {
    sendEvent('bank_account_action', {
        event_category: 'Bank Accounts',
        action,
        ...(source && { source }),
        ...(bankName && { bank_name: truncate(bankName, 50) }),
        description: `Bank account ${action}`,
    });
};

export const trackBankAccountActionFailed = (
    action: 'add' | 'activate' | 'deactivate',
    reason?: string,
    source?: string,
    bankName?: string,
) => {
    sendEvent('bank_account_action_failed', {
        event_category: 'Bank Accounts',
        action,
        ...(reason && { reason: truncate(reason) }),
        ...(source && { source }),
        ...(bankName && { bank_name: truncate(bankName, 50) }),
        description: `Bank account ${action} failed`,
    });
};

// Payment Method Events (Stripe card add)
export const trackPaymentMethodAdded = (type: 'card' | 'bank', source?: string) => {
    sendEvent('payment_method_added', {
        event_category: 'Payments',
        type,
        ...(source && { source }),
        description: `Payment method added: ${type}`,
    });
};

export const trackPaymentMethodAddFailed = (type: 'card' | 'bank', reason?: string) => {
    sendEvent('payment_method_add_failed', {
        event_category: 'Payments',
        type,
        ...(reason && { reason: truncate(reason) }),
        description: `Payment method add failed: ${type}`,
    });
};

// Settings / Profile Events (track only when details are actually updated)
export const trackProfileDetailsSaved = () => {
    sendEvent('profile_details_saved', {
        event_category: 'Settings',
        description: 'Profile details saved',
    });
};

export const trackProfileUpdateFailed = (field: string, reason?: string) => {
    sendEvent('profile_update_failed', {
        event_category: 'Settings',
        field,
        ...(reason && { reason: truncate(reason) }),
        description: `Profile update failed: ${field}`,
    });
};

export const trackPasswordChange = () => {
    sendEvent('password_change', {
        event_category: 'Settings',
        description: 'Password changed successfully.',
    });
};

export const trackPasswordChangeFailed = (reason?: string) => {
    sendEvent('password_change_failed', {
        event_category: 'Settings',
        ...(reason && { reason: truncate(reason) }),
        description: 'Password change failed',
    });
};

export const trackProfilePictureUpdated = () => {
    sendEvent('profile_picture_updated', {
        event_category: 'Settings',
        description: 'Profile picture updated',
    });
};

export const trackPhoneNumberAdded = () => {
    sendEvent('phone_number_added', {
        event_category: 'Settings',
        description: 'Phone number added',
    });
};

export const trackPhoneVerified = () => {
    sendEvent('phone_verified', {
        event_category: 'Settings',
        description: 'Phone number verified',
    });
};

export const trackPhoneVerificationFailed = (reason?: string) => {
    sendEvent('phone_verification_failed', {
        event_category: 'Settings',
        ...(reason && { reason: truncate(reason) }),
        description: 'Phone verification failed',
    });
};

export const trackNotificationPreferenceUpdated = (channel: 'email' | 'sms', enabled: boolean) => {
    sendEvent('notification_preference_updated', {
        event_category: 'Settings',
        channel,
        enabled,
        description: `Notification preference updated: ${channel} ${enabled ? 'enabled' : 'disabled'}`,
    });
};

export const trackLogout = (
    reason: 'manual' | 'session_timeout' | 'token_refresh_failed' | 'password_changed',
    userId?: string,
) => {
    sendEvent('logout', {
        event_category: 'Authentication',
        reason,
        ...(userId && { user_id: userId }),
        description: `User logged out: ${reason}`,
    });
};

export const trackRegistrationStarted = () => {
    sendEvent('registration_started', {
        event_category: 'Registration',
        description: 'Registration security check submitted',
    });
};

export const trackRegistrationOtpVerified = () => {
    sendEvent('registration_otp_verified', {
        event_category: 'Registration',
        description: 'Registration OTP verified',
    });
};

export const trackRegistrationOtpFailed = (reason?: string) => {
    sendEvent('registration_otp_failed', {
        event_category: 'Registration',
        ...(reason && { reason: truncate(reason) }),
        description: 'Registration OTP verification failed',
    });
};

export const trackRegistrationCompleted = () => {
    sendEvent('registration_completed', {
        event_category: 'Registration',
        description: 'New user registered',
    });
};

export const trackRegistrationFailed = (step: string, reason?: string) => {
    sendEvent('registration_failed', {
        event_category: 'Registration',
        step,
        ...(reason && { reason: truncate(reason) }),
        description: `Registration failed at step: ${step}`,
    });
};

// Account Migration Events
export const trackAccountMigrationStarted = () => {
    sendEvent('account_migration_started', {
        event_category: 'Registration',
        description: 'Account migration started',
    });
};

export const trackAccountMigrationCompleted = () => {
    sendEvent('account_migration_completed', {
        event_category: 'Registration',
        description: 'Account migration completed',
    });
};

export const trackAccountMigrationFailed = (step: string, reason?: string) => {
    sendEvent('account_migration_failed', {
        event_category: 'Registration',
        step,
        ...(reason && { reason: truncate(reason) }),
        description: `Account migration failed at step: ${step}`,
    });
};

// Export Events
export const trackExport = (exportType: string, dataType: string) => {
    sendEvent('export', {
        event_category: 'Export',
        export_type: exportType,
        data_type: dataType,
        description: `Exported ${dataType} as ${exportType}`,
    });
};

// Print Events
export const trackPrint = (documentType: string) => {
    sendEvent('print', {
        event_category: 'Print',
        document_type: documentType,
        description: `Printed: ${documentType}`,
    });
};

// Autopay Events
export const trackAutoPayModalOpen = (contractId: string) => {
    sendEvent('autopay_modal_open', {
        event_category: 'AutoPay',
        contract_id: contractId,
        description: 'AutoPay authorization modal opened',
    });
};

export const trackAutoPayEnabled = (contractId: string, bankName?: string) => {
    sendEvent('autopay_enabled', {
        event_category: 'AutoPay',
        contract_id: contractId,
        ...(bankName && { bank_name: truncate(bankName, 50) }),
        description: 'AutoPay enabled successfully',
    });
};

export const trackAutoPayFailed = (contractId: string, reason?: string) => {
    sendEvent('autopay_failed', {
        event_category: 'AutoPay',
        contract_id: contractId,
        failure_reason: reason || 'Unknown',
        description: 'AutoPay enablement failed',
    });
};

// Chatbot Events
export const trackChatbotOpened = (source?: string) => {
    sendEvent('chatbot_opened', {
        event_category: 'Chatbot',
        ...(source && { open_source: source }),
        description: 'Chatbot assistant opened',
    });
};

export const trackChatbotMessageSent = () => {
    sendEvent('chatbot_message_sent', {
        event_category: 'Chatbot',
        description: 'Chatbot message sent',
    });
};

export const trackChatbotQuickAction = (actionLabel: string, query?: string) => {
    sendEvent('chatbot_quick_action_click', {
        event_category: 'Chatbot',
        action_label: actionLabel,
        ...(query && { query: truncate(query, 100) }),
        description: `Chatbot quick action clicked: ${actionLabel}`,
    });
};

// Track user ID (when user logs in)
export const setUserId = (userId: string) => {
    console.log('[GA] setUserId', userId);
    ReactGA.set({ userId });
};

// Generic GA4-style event escape hatch (event name + arbitrary params) for call sites that don't
// warrant their own dedicated trackX() wrapper - currently only the login flow.
export const trackEvent = (name: string, params?: Record<string, unknown>) => {
    console.log('[GA Event]', name, params);
    ReactGA.event(name, params);
};
